import type { ReactNode } from 'react'
import { api } from '@/runtime'
import type { StorageBackup, StorageDisk, StorageHost, StorageSummary } from '@/db/api'
import { alertDot, toneClass, useBar } from '@/lib/colour'
import { backupHealth, fillOf, fmtBytes, hostHealth, pctOf, shareOf, usedWords } from '@/lib/storage'
import { fmtAgo } from '@/lib/time'
import { Skeleton } from './Skeleton'
import { HealthDot } from './ClusterBits'
import { Failed, SectionTitle, Tag, cx } from './ui'

/**
 * Storage, a part of the Infrastructure page (/infra, 2026-10-31; the owner:
 * "what's where across the NAS, proxmox nodes, storage boxes, hetzner
 * volumes, everything"). Online only: GET /api/storage (internal/storage, all
 * from Prometheus). What needs a look comes first, then each site's hosts with
 * a use bar per disk, then the backups and how fresh they are. A failed read
 * is Failed, a first read Skeletons; a later failure keeps the last answer.
 * The page reads it (views/Infra.tsx, with `loadStorage`) and hands it here,
 * since the Overview's estate cards judge the same answer.
 */

export const loadStorage = () => api.storage()

// backups listed before "And more"
const CAP = 10

const Calm = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <p className="card px-3 py-3 text-sm text-muted" data-testid={testId}>
    {children}
  </p>
)

/** A small grey chip that clips a long name (a PVC's can run to a hundred characters). */
const Chip = ({ children }: { children: string }) => (
  <span
    className={cx(
      'inline-flex h-5 items-center rounded-md px-1.5 text-[11px] font-medium max-w-full',
      toneClass.muted,
    )}
    title={children}
  >
    <span className="truncate">{children}</span>
  </span>
)

const mono = (s: string) => s.startsWith('/')

const DiskRow = ({ disk }: { disk: StorageDisk }) => {
  const share = shareOf(disk)
  const pct = pctOf(share)
  const sized = disk.size > 0
  const detail = [disk.detail, disk.objects ? `${disk.objects.toLocaleString('en-GB')} objects` : '']
    .filter(Boolean)
    .join(', ')
  return (
    <div className="mt-2.5" data-testid="storage-disk">
      <div className="flex items-baseline gap-2">
        <span
          className={cx(
            'flex-1 min-w-0 truncate leading-5',
            mono(disk.name) ? 'font-mono text-[12px]' : 'text-[13px]',
          )}
          title={disk.name}
        >
          {disk.name}
        </span>
        {disk.smart && (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-faint shrink-0"
            aria-label={disk.smart === 'ok' ? 'SMART passes' : 'SMART fails'}
          >
            <HealthDot health={disk.smart === 'ok' ? 'ok' : 'warn'} />
            SMART
          </span>
        )}
        <span className="text-[12px] text-muted tabular shrink-0">{usedWords(disk)}</span>
      </div>
      {sized && (
        <div
          className={cx('h-1.5 rounded-full overflow-hidden mt-1', useBar.track)}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label={`${disk.name}, ${pct}% full`}
        >
          <div
            className={cx('h-full rounded-full', useBar[fillOf(share)])}
            style={{ width: `${Math.max(1, Math.round(share * 100))}%` }}
          />
        </div>
      )}
      {(detail || sized) && (
        <div className="flex items-baseline gap-2 mt-0.5 text-[11px] text-faint">
          <span className={cx('flex-1 min-w-0 truncate', detail.startsWith('/dev') && 'font-mono')}>
            {detail}
          </span>
          {sized && <span className="tabular shrink-0">{pct}%</span>}
        </div>
      )}
    </div>
  )
}

export const HostRow = ({ host }: { host: StorageHost }) => (
  <li className="px-3 py-2.5" data-testid="storage-host">
    <div className="flex items-center gap-2 min-h-6">
      <HealthDot health={hostHealth(host)} />
      <p className="text-[15px] leading-5 font-medium break-words min-w-0">{host.name}</p>
      {host.role !== host.name && <Tag>{host.role}</Tag>}
      {host.down && <Tag tone="warn">Not reporting</Tag>}
    </div>
    <div className="pl-4">
      {host.note && <p className="text-[12px] text-muted leading-snug">{host.note}</p>}
      {host.down && (
        <p className="text-[13px] text-muted leading-snug mt-1">
          Its node exporter does not answer, so its disks are unknown.
        </p>
      )}
      {host.disks.map((d) => (
        <DiskRow key={d.name} disk={d} />
      ))}
      {host.disks_more && <p className="text-[12px] text-faint mt-2">And more</p>}
      {!!host.drives?.length && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-2.5">
          <span className="text-[11px] text-faint">Drives</span>
          {host.drives.map((d) => (
            <span
              key={d.name}
              className="inline-flex items-center gap-1 font-mono text-[12px]"
              aria-label={`${d.name}, SMART ${d.healthy ? 'passes' : 'fails'}`}
            >
              <HealthDot health={d.healthy ? 'ok' : 'warn'} />
              {d.name}
            </span>
          ))}
        </div>
      )}
      {!!host.volumes?.length && (
        <div className="flex flex-wrap items-center gap-1 mt-2.5">
          <span className="text-[11px] text-faint mr-1">Volumes</span>
          {host.volumes.map((v) => (
            <Chip key={v}>{v}</Chip>
          ))}
          {host.volumes_more && <span className="text-[11px] text-faint">and more</span>}
        </div>
      )}
    </div>
  </li>
)

const backupLine = (b: StorageBackup): string => {
  if (b.kind === 'postgres') return `Postgres base backup, ${b.where.toLowerCase()}`
  const parts = [b.where]
  if (b.size) parts.push(fmtBytes(b.size))
  if (b.snapshots) parts.push(b.snapshots === 1 ? '1 snapshot' : `${b.snapshots} snapshots`)
  return parts.join(', ')
}

export const BackupRow = ({ backup: b, now }: { backup: StorageBackup; now: number }) => (
  <li className="flex items-start gap-2.5 px-3 py-2.5 min-h-12" data-testid="storage-backup">
    <span className="flex mt-[6px]">
      <HealthDot health={backupHealth(b)} />
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2">
        <p className="flex-1 min-w-0 text-[15px] leading-5 font-medium break-words">{b.name}</p>
        <span className="text-[12px] text-muted tabular shrink-0">
          {b.last ? fmtAgo(b.last, now) : 'Never'}
        </span>
      </div>
      <p className="text-[12px] text-muted leading-snug mt-0.5">{backupLine(b)}</p>
      {(b.offsite || b.stale || b.offsite_stale) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {b.stale && <Tag tone="warn">{b.last ? 'Over 26 hours old' : 'Never succeeded'}</Tag>}
          {b.offsite_stale && <Tag tone="warn">Offsite copy over 26 hours old</Tag>}
          {b.offsite && !b.offsite_stale && (
            <span className="text-[11px] text-faint">Offsite copy {fmtAgo(b.offsite, now)}</span>
          )}
        </div>
      )}
    </div>
  </li>
)

const Summary = ({ data, now }: { data: StorageSummary; now: number }) => (
  <>
    {data.warnings.length > 0 ? (
      <ul className="group-list mb-4" data-testid="storage-warnings">
        {data.warnings.map((w) => (
          <li key={w.text} className="flex items-start gap-2.5 px-3 py-2.5 min-h-11">
            <span
              className={cx('h-2 w-2 rounded-full shrink-0 mt-[6px]', alertDot[w.severity])}
              aria-label={w.severity}
            />
            <p className="flex-1 min-w-0 text-[14px] leading-5 break-words">{w.text}</p>
          </li>
        ))}
      </ul>
    ) : (
      <div className="mb-4">
        <Calm testId="storage-calm">
          Every disk has room, every drive passes SMART and every backup is fresh.
        </Calm>
      </div>
    )}
    {data.sites.map((s) => (
      <div key={s.id} className="mb-4" data-testid={`storage-site-${s.id}`}>
        <SectionTitle>{s.name}</SectionTitle>
        <ul className="group-list">
          {s.hosts.map((h) => (
            <HostRow key={h.name} host={h} />
          ))}
        </ul>
      </div>
    ))}
    {data.backups.length > 0 && (
      <div data-testid="storage-backups">
        <SectionTitle>Backups</SectionTitle>
        <ul className="group-list">
          {data.backups.slice(0, CAP).map((b) => (
            <BackupRow key={`${b.kind}/${b.name}`} backup={b} now={now} />
          ))}
          {data.backups.length > CAP && (
            <li className="px-3 min-h-10 flex items-center text-[13px] text-faint">And more</li>
          )}
        </ul>
      </div>
    )}
  </>
)

export const Storage = ({
  data,
  error,
  reload,
  now,
}: {
  data: StorageSummary | null
  error: string
  reload: () => void
  now: number
}) => {
  if (data) return <Summary data={data} now={now} />
  if (error) return <Failed what="the storage overview" reason={error} onRetry={reload} />
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading" data-testid="storage-loading">
      <Skeleton className="h-14" />
      <Skeleton className="h-56" />
      <Skeleton className="h-32" />
    </div>
  )
}
